/**
 * Pilot Onboarding Drip — status, per-step analytics, and email history
 * with a "mark replied" action so ops can log inbound replies until we
 * ingest Resend inbound webhooks.
 *
 * Feeds off:
 *   GET   /api/admin/pilot-onboarding/scheduler-status
 *   GET   /api/admin/pilot-onboarding/analytics
 *   GET   /api/admin/pilot-onboarding/history
 *   GET   /api/admin/pilot-onboarding/candidates
 *   POST  /api/admin/pilot-onboarding/run-batch
 *   PATCH /api/admin/pilot-onboarding/{id}/mark-replied
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkle, CheckCircle, XCircle, Eye, ChatCircle } from "@phosphor-icons/react";
import client from "../lib/api";

const STEP_LABELS = { day0: "Day 0 · Welcome", day3: "Day 3 · Personalized", day7: "Day 7 · Feedback" };
const STATUS_STYLE = {
  sent: "text-emerald-300 border-emerald-400/40 bg-emerald-400/[0.06]",
  skipped: "text-neutral-400 border-neutral-700 bg-neutral-800/40",
  failed: "text-red-300 border-red-400/40 bg-red-400/[0.06]",
  queued: "text-sky-300 border-sky-400/40 bg-sky-400/[0.06]",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function AdminPilotDripPage() {
  const [scheduler, setScheduler] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [abReport, setAbReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [testReminder, setTestReminder] = useState({ to_email: "", to_phone: "", name: "", date: "", time: "14:00" });
  const [sendingTest, setSendingTest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, a, ab, h, c, b] = await Promise.all([
        client.get("/admin/pilot-onboarding/scheduler-status"),
        client.get("/admin/pilot-onboarding/analytics"),
        client.get("/admin/pilot-onboarding/ab-report"),
        client.get("/admin/pilot-onboarding/history"),
        client.get("/admin/pilot-onboarding/candidates"),
        client.get("/admin/pilot-bookings"),
      ]);
      setScheduler(s.data);
      setAnalytics(a.data);
      setAbReport(ab.data);
      setHistory(h.data?.emails || []);
      setCandidates(c.data?.candidates || []);
      setBookings(b.data?.bookings || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load drip status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runBatch = async () => {
    setRunning(true);
    try {
      const r = await client.post("/admin/pilot-onboarding/run-batch");
      toast.success(`Processed ${r.data?.processed || 0} · sent ${r.data?.sent || 0}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed");
    } finally {
      setRunning(false);
    }
  };

  const markReplied = async (id) => {
    try {
      await client.patch(`/admin/pilot-onboarding/${id}/mark-replied`);
      toast.success("Marked as replied");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const lockVariant = async (stepKey, variantKey) => {
    try {
      const r = await client.post(`/admin/pilot-onboarding/ab-lock/${stepKey}/${variantKey}`);
      toast.success(
        variantKey === "clear"
          ? `Rotation resumed for ${stepKey}`
          : `Locked ${stepKey} → variant ${r.data.locked_variant}`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lock failed");
    }
  };

  const sendTestReminder = async () => {
    if (!testReminder.to_email.trim()) {
      toast.error("Enter an email to send the preview to");
      return;
    }
    setSendingTest(true);
    try {
      const r = await client.post("/admin/pilot-bookings/reminders/send-test", testReminder);
      const ok = r.data?.email_result?.sent;
      toast[ok ? "success" : "error"](
        ok ? `Preview sent to ${r.data.sent_to}` : `Email failed: ${r.data?.email_result?.reason || "unknown"}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview send failed");
    } finally {
      setSendingTest(false);
    }
  };

  const markHeld = async (bid) => {
    try {
      await client.post(`/admin/pilot-bookings/${bid}/mark-held`);
      toast.success("Marked as held — no-show follow-up suppressed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark-held failed");
    }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="admin-pilot-drip-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1 text-[#FF9764] flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> Founding Pilot · Onboarding Drip
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Day 0 · 3 · 7 email cadence
          </h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-2xl">
            Auto-fires the moment a pilot flips to <span className="text-neutral-300">pilot_onboarded</span>.
            Tracks opens via 1×1 pixel and replies via manual admin flag until Resend inbound webhooks are wired.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="drip-refresh"
            className="text-xs uppercase tracking-widest text-neutral-500 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-3 py-1.5"
          >
            Refresh
          </button>
          <button
            onClick={runBatch}
            disabled={running}
            data-testid="drip-run-batch"
            className="btn-primary text-xs uppercase tracking-widest rounded-full px-4 py-1.5 disabled:opacity-50"
          >
            {running ? "Running…" : "Run batch now"}
          </button>
        </div>
      </div>

      {/* Scheduler row */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4" data-testid="drip-scheduler-cards">
        <StatCard label="Scheduler" value={scheduler?.running ? "Running" : "Stopped"} accent={scheduler?.running ? "emerald" : "red"} />
        <StatCard label="Interval" value={scheduler ? `${Math.round((scheduler.interval_seconds || 0) / 60)} min` : "—"} />
        <StatCard label="Due now" value={candidates.length} accent={candidates.length ? "orange" : "neutral"} />
        <StatCard label="Last run" value={fmtDateTime(scheduler?.last_run_at)} />
      </div>

      {/* Per-step analytics */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Per-step engagement</div>
        <div className="grid gap-3 sm:grid-cols-3" data-testid="drip-analytics-grid">
          {Object.entries(analytics?.per_step || {}).map(([key, s]) => (
            <div key={key} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold mb-2">
                {STEP_LABELS[key] || key}
              </div>
              <div className="text-2xl font-semibold tabular-nums">{s.sent}</div>
              <div className="text-[11px] text-neutral-500 mb-3">sent</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <MiniStat label="Opens" value={`${s.opened} · ${s.open_rate_pct}%`} accent="text-sky-300" />
                <MiniStat label="Replies" value={`${s.replied} · ${s.reply_rate_pct}%`} accent="text-emerald-300" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* A/B subject variants */}
      <div data-testid="drip-ab-section">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2 flex items-center justify-between">
          <span>A/B subject variants</span>
          <span className="text-neutral-600 normal-case tracking-normal">
            Auto-lock ≥ {abReport?.per_step?.day0?.min_sends_needed || 10} sends/variant + {abReport?.per_step?.day0?.win_margin_pct || 20}% open-rate margin
          </span>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {Object.entries(abReport?.per_step || {}).map(([key, s]) => (
            <div key={key} className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`ab-card-${key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold">
                  {STEP_LABELS[key] || key}
                </div>
                <div className="flex items-center gap-2">
                  {s.confidence && (
                    <DripConfidenceChip conf={s.confidence} stepKey={key} />
                  )}
                  {s.locked_variant ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-400/40 bg-emerald-400/[0.06] rounded-full px-2 py-0.5">
                      Locked · {s.locked_variant}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-neutral-500 border border-white/10 rounded-full px-2 py-0.5">
                      Rotating
                    </span>
                  )}
                </div>
              </div>
              {Object.entries(s.variants).map(([vk, v]) => (
                <div key={vk} className={`rounded-lg border p-2.5 mb-2 ${s.locked_variant === vk ? "border-emerald-400/40 bg-emerald-400/[0.04]" : "border-white/5 bg-black/40"}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded ${s.locked_variant === vk ? "bg-emerald-400/20 text-emerald-300" : "bg-white/5 text-neutral-400"}`}>{vk}</span>
                    <button
                      onClick={() => lockVariant(key, vk)}
                      disabled={s.locked_variant === vk}
                      data-testid={`ab-lock-${key}-${vk}`}
                      className="ml-auto text-[10px] text-neutral-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {s.locked_variant === vk ? "Locked" : "Lock this"}
                    </button>
                  </div>
                  <div className="text-[11px] text-white leading-snug font-medium truncate" title={v.subject}>
                    {v.subject}
                  </div>
                  {v.body_intro && (
                    <div className="text-[10px] text-neutral-500 leading-snug mt-1 line-clamp-2" title={v.body_intro}>
                      {v.body_intro}
                    </div>
                  )}
                  <div className="flex gap-3 text-[10px] text-neutral-500 mt-2 pt-2 border-t border-white/5">
                    <span><span className="text-white">{v.sent}</span> sent</span>
                    <span><span className="text-sky-300">{v.open_rate_pct}%</span> open</span>
                    <span><span className="text-emerald-300">{v.reply_rate_pct}%</span> reply</span>
                  </div>
                </div>
              ))}
              {s.locked_variant && (
                <button
                  onClick={() => lockVariant(key, "clear")}
                  data-testid={`ab-clear-${key}`}
                  className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white mt-1"
                >
                  Resume rotation
                </button>
              )}
              {s.auto_lock_message && (
                <div className="mt-2 text-[10px] text-emerald-300 italic">
                  {s.auto_lock_message}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Booking reminder preview */}
      <div data-testid="reminder-preview-section" className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">
          Booking Reminder Preview
        </div>
        <div className="text-sm text-white mb-3">Send yourself a test 24-hour reminder</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <input
            type="email"
            placeholder="Send to email"
            value={testReminder.to_email}
            onChange={(e) => setTestReminder({ ...testReminder, to_email: e.target.value })}
            data-testid="reminder-preview-email"
            className="col-span-2 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#FF5A1F]/50 focus:outline-none placeholder:text-neutral-600"
          />
          <input
            type="tel"
            placeholder="(optional phone)"
            value={testReminder.to_phone}
            onChange={(e) => setTestReminder({ ...testReminder, to_phone: e.target.value })}
            data-testid="reminder-preview-phone"
            className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#FF5A1F]/50 focus:outline-none placeholder:text-neutral-600"
          />
          <input
            type="date"
            value={testReminder.date}
            onChange={(e) => setTestReminder({ ...testReminder, date: e.target.value })}
            data-testid="reminder-preview-date"
            className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#FF5A1F]/50 focus:outline-none"
          />
          <select
            value={testReminder.time}
            onChange={(e) => setTestReminder({ ...testReminder, time: e.target.value })}
            data-testid="reminder-preview-time"
            className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#FF5A1F]/50 focus:outline-none"
          >
            {["10:00", "12:00", "14:00", "16:00"].map((t) => <option key={t} value={t}>{t} CT</option>)}
          </select>
        </div>
        <button
          onClick={sendTestReminder}
          disabled={sendingTest}
          data-testid="reminder-preview-send"
          className="mt-3 btn-primary text-xs uppercase tracking-widest rounded-full px-4 py-2 disabled:opacity-50"
        >
          {sendingTest ? "Sending…" : "Send test reminder"}
        </button>
      </div>

      {/* Recent bookings with Mark-held */}
      <div data-testid="bookings-section">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2 flex items-center justify-between">
          <span>Recent bookings</span>
          <span className="text-neutral-600 normal-case tracking-normal">
            Mark held on any row to suppress the no-show follow-up
          </span>
        </div>
        {bookings.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 py-8 text-center text-sm text-neutral-500" data-testid="bookings-empty">
            No bookings yet.
          </div>
        ) : (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <div className="grid grid-cols-[110px_1fr_100px_100px_120px] gap-3 px-4 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold border-b border-white/5">
              <div>Date/Time</div><div>Applicant</div><div>Status</div><div>Reminded</div><div className="text-right">Action</div>
            </div>
            <div className="divide-y divide-white/5 max-h-[420px] overflow-y-auto" data-testid="bookings-list">
              {bookings.slice(0, 50).map((b) => {
                const status = b.status || "booked";
                const held = status === "held";
                const cancelled = status === "cancelled" || status === "rescheduled";
                const noshowed = !!b.noshow_followup_sent_at && !held;
                return (
                  <div key={b.id} className="grid grid-cols-[110px_1fr_100px_100px_120px] gap-3 items-center px-4 py-3 text-sm" data-testid={`booking-row-${b.id}`}>
                    <div className="text-[11px] text-neutral-400 tabular-nums">
                      {b.date}<br />
                      <span className="text-neutral-600">{b.time} CT</span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-white truncate">
                        {b.name || "—"} <span className="text-neutral-500 font-normal">· {b.company_name || "no company"}</span>
                      </div>
                      <div className="text-[11px] text-neutral-500 truncate">{b.email}</div>
                    </div>
                    <div>
                      {held ? (
                        <span className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-emerald-400/40 bg-emerald-400/[0.06] text-emerald-300">Held</span>
                      ) : noshowed ? (
                        <span className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-red-400/40 bg-red-400/[0.06] text-red-300">Ghosted</span>
                      ) : cancelled ? (
                        <span className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-neutral-600 bg-neutral-800/40 text-neutral-500">{status}</span>
                      ) : (
                        <span className="inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-sky-400/40 bg-sky-400/[0.06] text-sky-300">Booked</span>
                      )}
                    </div>
                    <div className="text-[11px] text-neutral-500">
                      {b.reminded_at ? <span className="text-neutral-300">✓</span> : <span className="text-neutral-700">—</span>}
                    </div>
                    <div className="text-right">
                      {held ? (
                        <span className="text-[11px] text-neutral-600">✓ held</span>
                      ) : cancelled ? (
                        <span className="text-[11px] text-neutral-600">—</span>
                      ) : (
                        <button
                          onClick={() => markHeld(b.id)}
                          data-testid={`booking-mark-held-${b.id}`}
                          className="text-[11px] uppercase tracking-widest text-neutral-400 hover:text-emerald-300 inline-flex items-center gap-1 border border-white/10 hover:border-emerald-400/40 rounded-full px-2.5 py-1"
                        >
                          <CheckCircle size={11} weight="bold" /> Mark held
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Due-now list */}
      {candidates.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
            Due to fire right now
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5" data-testid="drip-candidates">
            {candidates.map((c) => (
              <div key={`${c.pilot_id}-${c.step_key}`} className="flex items-center gap-3 p-3 text-sm">
                <span className="inline-block px-2 py-0.5 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.06] text-[#FF9764] text-[10px] uppercase tracking-widest font-semibold">
                  {c.step_key}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-white truncate">{c.contact_name} <span className="text-neutral-500 font-normal">· {c.company_name}</span></div>
                  <div className="text-[11px] text-neutral-500 truncate">{c.email} · {c.subject}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* History */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">Send history</div>
        {loading ? (
          <div className="text-sm text-neutral-500 py-8 text-center">Loading…</div>
        ) : history.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 py-12 text-center text-sm text-neutral-500" data-testid="drip-history-empty">
            No drip emails have fired yet.
          </div>
        ) : (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <div className="grid grid-cols-[70px_1fr_100px_60px_60px_120px] gap-3 px-4 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold border-b border-white/5">
              <div>Step</div><div>Recipient</div><div>Status</div><div>Opens</div><div>Reply</div><div>Sent</div>
            </div>
            <div className="divide-y divide-white/5 max-h-[520px] overflow-y-auto" data-testid="drip-history-list">
              {history.map((h) => {
                const stStyle = STATUS_STYLE[h.status] || STATUS_STYLE.queued;
                return (
                  <div key={h.id} className="grid grid-cols-[70px_1fr_100px_60px_60px_120px] gap-3 items-center px-4 py-3 text-sm hover:bg-white/[0.02] transition-colors" data-testid={`drip-history-row-${h.id}`}>
                    <div className="text-[11px] font-mono text-[#FF9764]">{h.step_key}</div>
                    <div className="min-w-0">
                      <div className="text-white truncate">{h.email}</div>
                      <div className="text-[11px] text-neutral-500 truncate">{h.subject}</div>
                    </div>
                    <div>
                      <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${stStyle}`}>
                        {h.status}
                      </span>
                    </div>
                    <div className="text-[11px] text-neutral-400 flex items-center gap-1">
                      {h.opened_at ? <><Eye size={11} weight="fill" className="text-sky-300" />{h.open_count || 1}</> : <span className="text-neutral-600">—</span>}
                    </div>
                    <div className="text-[11px]">
                      {h.replied_at ? (
                        <span className="inline-flex items-center gap-1 text-emerald-300">
                          <CheckCircle size={12} weight="fill" />
                        </span>
                      ) : h.status === "sent" ? (
                        <button
                          onClick={() => markReplied(h.id)}
                          data-testid={`drip-mark-replied-${h.id}`}
                          className="text-neutral-500 hover:text-emerald-300 inline-flex items-center gap-1 text-[11px]"
                          title="Mark this touch as replied-to"
                        >
                          <ChatCircle size={11} weight="bold" /> Mark
                        </button>
                      ) : (
                        <XCircle size={12} className="text-neutral-700" />
                      )}
                    </div>
                    <div className="text-[11px] text-neutral-500 tabular-nums">{fmtDateTime(h.sent_at || h.created_at)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ label, value, accent }) => {
  const map = {
    emerald: "text-emerald-300",
    orange: "text-[#FF9764]",
    red: "text-red-300",
    neutral: "text-neutral-300",
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-1">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${map[accent] || "text-white"}`}>{value}</div>
    </div>
  );
};

const MiniStat = ({ label, value, accent }) => (
  <div className="rounded-md bg-black/40 border border-white/5 px-2 py-1.5">
    <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
    <div className={`text-xs font-semibold tabular-nums ${accent || "text-white"}`}>{value}</div>
  </div>
);

// Two-proportion z-test result chip for the A/B report — same visual
// language as the nurture page so the admin gets a consistent read across
// both cadences.
function DripConfidenceChip({ conf, stepKey }) {
  if (!conf) return null;
  const { confidence_pct, too_early, label } = conf;
  const tone = too_early || confidence_pct == null
    ? "border-white/10 bg-white/[0.03] text-neutral-500"
    : confidence_pct >= 95
    ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
    : confidence_pct >= 80
    ? "border-amber-300/40 bg-amber-300/10 text-amber-200"
    : "border-white/10 bg-white/[0.03] text-neutral-400";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${tone}`}
      data-testid={`drip-ab-confidence-${stepKey}`}
      title={label}
    >
      {label}
    </span>
  );
}
